import type { Data, UID } from '@repo/strapi-types'
import type { useTranslations } from 'next-intl'
import type React from 'react'
import type { AppLinkHrefMode } from '@/components/elementary/AppLink/types'
import type { ContainerProps } from '@/components/elementary/Container'
import type { routing } from '@/i18n/routing'
import type { IntlKeysPath } from './helpers'

// Use type safe message keys with `next-intl`
type Messages = typeof import('../../messages/en.json')
// eslint-disable-next-line no-unused-vars
export interface IntlMessages extends Messages {}

export type AppLocale = (typeof routing.locales)[number]

export type AppError = Error & {
  message: string | number
  status: number
  name?: string
  details?: Record<string, unknown>
  translateKeyPrefixForErrors?: Parameters<typeof useTranslations>[0]
}

export type NextMetadataTwitterCard = 'summary' | 'summary_large_image' | 'player' | 'app'
export interface AppLink {
  href: string
  target?: string
  label?: string
  translateKey?: IntlKeysPath
}

export interface IconProps {
  className?: string
}

export type MetadataTypes = Extract<
  UID.ContentType,
  | 'api::page.page'
  | 'api::blog-article.blog-article'
  | 'api::blog.blog'
  | 'api::blog-category.blog-category'
  | 'api::blog-author.blog-author'
  | 'api::case-study.case-study'
  | 'api::case-study-page.case-study-page'
  | 'api::apps-page.apps-page'
  | 'api::app.app'
  | 'api::app-category.app-category'
>

export type OverviewContentTypeUid = Extract<
  MetadataTypes,
  'api::blog-article.blog-article' | 'api::case-study.case-study' | 'api::app.app'
>

export type TranslateFn = {
  // biome-ignore lint/suspicious/noExplicitAny: Allows accepting translation functions from any namespace
  (key: any, values?: any): string
  // biome-ignore lint/suspicious/noExplicitAny: Allows accepting translation functions from any namespace
  rich(key: any, values?: any): React.ReactNode
  // biome-ignore lint/suspicious/noExplicitAny: Allows accepting translation functions from any namespace
  markup(key: any, values?: any): string
  // biome-ignore lint/suspicious/noExplicitAny: Allows accepting translation functions from any namespace
  raw(key: any): unknown
  // biome-ignore lint/suspicious/noExplicitAny: Allows accepting translation functions from any namespace
  has(key: any): boolean
}

export type TestIds<T extends Record<string, string | null>> = {
  COMPONENT: string | null
} & T

export function createTestIds<T extends Record<string, string>>(ids: TestIds<T>) {
  return ids
}

export interface GridCardProps {
  id?: string | number
  documentId?: string | number
  icon?: Data.Component<'utilities.basic-image'> | null
  image?: Data.Component<'utilities.basic-image'> | null
  title?: string | null
  author?: string | null
  description?: string | null
  categories?:
    | Data.ContentType<'api::app-category.app-category'>[]
    | Data.ContentType<'api::library-item-category.library-item-category'>[]
    | Data.ContentType<'api::partner-item-category.partner-item-category'>[]
    | null
  link?: Data.Component<'utilities.link'> | null
  tag?: Data.Component<'utilities.tag'> | null
  isFeatured?: boolean
}

export type GridCategoryNavigationItem = {
  title: string
  link: string
  hrefMode?: AppLinkHrefMode
  documentId: string
  fullPath: string
  slug?: string | null
}
export type GridCategoryNavigationData = {
  allCategories: GridCategoryNavigationItem[]
  topCategories: GridCategoryNavigationItem[]
  restCategories: GridCategoryNavigationItem[]
  additionalLink?: Data.Component<'utilities.link'> | null
}

export type GridTypeProps = 'apps' | 'partners' | 'library'

export type SearchableArticle = Data.ContentType<'api::blog-article.blog-article'> & {
  stringifiedContent: string
}

/*
 * The context helps us define styling based on page context. Blog articles have a different heading styling than landing pages etc.
 *
 * blog = blog article content
 * lokalog = lokalise changelog content
 * case-study = case study landing pages
 * integration-card = app integration cards
 * landing = general landing pages
 * section = any section on blog or landing pages
 * default = other pages
 *
 */

export type LayoutContext =
  | 'blog'
  | 'lokalog'
  | 'case-study'
  | 'apps-category'
  | 'apps-category-detail'
  | 'integration-card'
  | 'apps-card'
  | 'landing'
  | 'section'
  | 'darkTheme'
  | 'default' // Frontend layout context

export type ComponentLayout = {
  gapClassName?: string
  containerPaddingX?: ContainerProps['paddingX']
  containerPaddingY?: ContainerProps['paddingY']
  containerPaddingTop?: ContainerProps['paddingTop']
  containerPaddingBottom?: ContainerProps['paddingBottom']
}
